import {
  publishEventParameter,
  publishEvents,
} from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { getLocaleCookie } from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';
import { UseFormSetError } from 'react-hook-form';

import {
  ga_fare_estimate_form_validation,
  service_category_partload,
} from '../domain/configs/Store.store';
import { FormType } from '../domain/entities/FareEstimateForm';
import { capturePartloadLead } from '../domain/repo/PartloadLeadCaptureRepo';
import { checkPartloadServiceability } from '../domain/repo/PartloadServiceabilityRepo';
import { partload_submit_error } from '../domain/store/FareEstimateStore.store';
import {
  convertToFareEstimateForm,
  getCityFromStorage,
  publishClickAnalytics,
  publishSuccessAnalytics,
} from './FareEstimateHelper';
import { getUtmParams, parseUtmParams } from './FareEstimateUtmParamsHelper';
import { FormData } from './FareEstimateValidationSchema';

export function buildPtlEventPayload(form_data: FormData): string {
  const cityName = getCityFromStorage('') ?? '';
  return `${cityName}_${form_data.name}_${form_data.mobile}_${form_data.requirement}_${form_data.pickup.landmark}_${form_data.drop.landmark}`;
}

function getServiceabilityErrorType(
  showPickupError: boolean | string | undefined,
  showDropError: boolean | string | undefined,
): string {
  if (showPickupError && showDropError) {
    return 'both';
  }
  if (showPickupError) {
    return 'pickup';
  }
  return 'drop';
}

export function handlePartloadNotServiceable(
  result: unknown,
  form_data: FormData,
  setError: UseFormSetError<FormData>,
): void {
  const fieldErrors =
    result !== null && typeof result === 'object' && 'fieldErrors' in result
      ? (result as { fieldErrors: { pickup?: string; drop?: string } })
          .fieldErrors
      : null;
  const showPickupError = !fieldErrors || fieldErrors.pickup;
  const showDropError = !fieldErrors || fieldErrors.drop;

  const errorType = getServiceabilityErrorType(showPickupError, showDropError);
  publishEventParameter('ptl_ns_error_loaded', {
    ptl_ns_error_parameter: `${errorType}_${buildPtlEventPayload(form_data)}`,
  });

  if (showPickupError) {
    setError('pickup', {
      type: 'manual',
      message: 'Pickup is not serviceable',
    });
  }
  if (showDropError) {
    setError('drop', {
      type: 'manual',
      message: 'Drop is not serviceable',
    });
  }
}

export async function submitPartloadLead(
  form_data: FormData,
  setError: UseFormSetError<FormData>,
  callback: (error: string) => void,
  category: string,
  on_lead_success_handler: (show: boolean) => void,
): Promise<void> {
  const locale = getLocaleCookie();
  const utmObject = parseUtmParams(getUtmParams());

  publishClickAnalytics(
    locale,
    form_data,
    utmObject,
    category,
    FormType.Partload,
  );

  const formData = convertToFareEstimateForm(form_data, FormType.Partload);

  publishEvents(
    category,
    ga_fare_estimate_form_validation,
    `${service_category_partload}_${formData.mobile}`,
  );

  try {
    const isServiceable = await checkPartloadServiceability(
      formData.pickupAddress.location.lat,
      formData.pickupAddress.location.lng,
      formData.dropAddress.location.lat,
      formData.dropAddress.location.lng,
      formData.mobile,
    );

    if (!isServiceable) {
      handlePartloadNotServiceable(null, form_data, setError);
      return;
    }

    const isLeadCaptured = await capturePartloadLead(formData);
    if (!isLeadCaptured) {
      callback(partload_submit_error);
      return;
    }

    on_lead_success_handler(true);
    publishEventParameter('ptl_successfull_lead_created', {
      ptl_successfull_lead_parameter: buildPtlEventPayload(form_data),
    });
    publishSuccessAnalytics(
      locale,
      form_data,
      utmObject,
      category,
      FormType.Partload,
    );
  } catch (error) {
    callback(
      error instanceof Error ? error.message : 'Unexpected error occurred',
    );
  }
}
